import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { DjangoService, Routes } from '@core/services/django.service';
import { EventTrackerService } from '@core/services/event-tracker.service';
import { AnalyticsEventName } from '@core/utils/AnalyticsEvents';
import {
    Configurations,
    LazyPictureComponent,
    Stops,
} from '@features/lazy-image/components/lazy-picture/lazy-picture.component';
import { ModalService } from '@features/modal/services/modal.service';
import { Itinerary, Trip } from '@generated/graphql';
import { CustomDepartureLeadCreateRequest } from '@generated/openapi/model/customDepartureLeadCreateRequest';
import { DepartureLeadCreateRequest } from '@generated/openapi/model/departureLeadCreateRequest';
import { LeadRequestAdditionalData } from '@generated/openapi/model/leadRequestAdditionalData';
import { LeadRequestCreatePolymorphicTypes as LeadRequestType } from '@generated/openapi/model/leadRequestCreatePolymorphicTypes';
import { PublicDeparture } from '@generated/openapi/model/publicDeparture';
import { EnquireFormAdditionalInfoModalComponent } from '@shared/enquire-form-additional-info-modal/enquire-form-additional-info-modal.component';
import { FormDynamicWithConfigComponent } from '@shared/form-dynamic-with-config/form-dynamic-with-config.component';

const groupSizeStrings = [
    'Solo traveller',
    'Couple / 2 friends',
    'Small group (up to 8)',
    'Large group (8+)',
];
const GROUP_SIZE_OPTIONS = [
    ...groupSizeStrings.map(value => ({
        value: value.toLowerCase(),
        label: value,
    })),
];

@Component({
    selector: 'app-enquire-form-modal',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        FormDynamicWithConfigComponent,
        LazyPictureComponent,
    ],
    templateUrl: './enquire-form-modal.component.html',
    styles: ``,
})
export class EnquireFormModalComponent implements OnInit {
    modalData: {
        trip: Trip;
        departure?: PublicDeparture;
        typeOfEnquiry: LeadRequestType;
        itinerary?: Itinerary;
        analyticsEventSent: AnalyticsEventName;
        analyticsEventCompleted: AnalyticsEventName;
        analyticsEventAbandoned: AnalyticsEventName;
        analyticsEventData: {
            trip: string;
            departure?: string;
            itinerary?: string;
        };
    };
    featuredImageConfiguration: Configurations;
    readonly modalContainerstyles = [
        '!w-full !h-full !max-h-screen !rounded-none',
    ];
    readonly isModalFullscreen = true;

    monthNames = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];
    years = [
        new Date().getUTCFullYear(),
        new Date().getUTCFullYear() + 1,
        new Date().getUTCFullYear() + 2,
        new Date().getUTCFullYear() + 3,
    ];

    formSchema = [
        {
            label: 'Your Name',
            type: 'text',
            name: 'name',
            validators: [Validators.required, Validators.minLength(3)],
        },
        {
            label: 'Your Email',
            type: 'email',
            name: 'email',
            validators: [Validators.required, Validators.email],
        },
        {
            label: 'No. of Travellers',
            name: 'travel',
            type: 'dropdown',
            testId: 'groupSize',
            placeholder: 'Select number',
            validators: [Validators.required],
            options: GROUP_SIZE_OPTIONS,
        },
    ];

    constructor(
        private modalService: ModalService,
        private djangoService: DjangoService,
        private eventTrackerService: EventTrackerService
    ) {}

    ngOnInit() {
        const trip = this.modalData.trip;
        const mobileCoverImage =
            trip?.enquireFormHorizontalImage || trip?.secondaryFeaturedImage;
        const desktopCoverImage =
            trip?.enquireFormVerticalImage || trip?.featuredImage;

        const currentMonth = new Date().getUTCMonth();
        const currentYear = new Date().getUTCFullYear();

        const dateOptions = this.years.flatMap(year => {
            return this.monthNames
                .map((month, index) => {
                    if (year === currentYear && index < currentMonth) {
                        return null;
                    }
                    return {
                        value: `${month} ${year}`,
                        label: `${month} ${year}`,
                    };
                })
                .filter(option => option !== null);
        });

        if (
            this.modalData.typeOfEnquiry ===
            LeadRequestType.CustomDepartureLeadRequest
        ) {
            this.formSchema.push({
                label: 'When do you want to travel?',
                name: 'departure_date',
                type: 'dropdown',
                testId: 'departureDate',
                placeholder: 'Select number',
                validators: [Validators.required],
                options: dateOptions,
            });
        }

        this.featuredImageConfiguration = {
            [Stops.FALLBACK]: {
                image: mobileCoverImage,
                ratioHeight: 1,
                ratioWidth: 1,
                width: 600,
                width2x: 800,
            },
            [Stops.BASE]: {
                image: mobileCoverImage,
                ratioHeight: 1,
                ratioWidth: 3,
                width: 600,
                width2x: 800,
            },
            [Stops.SM]: {
                image: mobileCoverImage,
                ratioHeight: 1,
                ratioWidth: 3,
                width: 640,
                width2x: 1000,
            },
            [Stops.MD]: {
                image: desktopCoverImage,
                ratioHeight: 4,
                ratioWidth: 1,
                width: 500,
                width2x: 500,
            },
            [Stops.LG]: {
                image: desktopCoverImage,
                ratioHeight: 4,
                ratioWidth: 1,
                width: 500,
                width2x: 500,
            },
            [Stops.XL]: {
                image: desktopCoverImage,
                ratioHeight: 4,
                ratioWidth: 1,
                width: 500,
                width2x: 500,
            },
            [Stops.XXL]: {
                image: desktopCoverImage,
                ratioHeight: 4,
                ratioWidth: 1,
                width: 500,
                width2x: 500,
            },
        };
    }

    proceedToNextPage(leadId: string) {
        this.modalService.show(EnquireFormAdditionalInfoModalComponent, {
            createdLeadId: leadId,
            ...this.modalData,
        });
    }

    submitPrimaryForm(values) {
        this.createLead({
            resourcetype: this.modalData.typeOfEnquiry,
            email: values.email,
            first_name: values.name,
            group_size: values.travel,
            trip: this.modalData?.trip?.linkedVirtualOfficeTrip || null,
            itinerary:
                this.modalData?.itinerary?.linkedVirtualOfficeItinerary || null,
            departure: this.modalData?.departure?.id || null,
            departure_date: values.departure_date,
        }).subscribe(response => {
            this.eventTrackerService.trackEvent(
                this.modalData.analyticsEventSent,
                this.modalData.analyticsEventData,
                values.email
            );
            this.proceedToNextPage(response.id);
        });
    }

    createLead(
        data: DepartureLeadCreateRequest | CustomDepartureLeadCreateRequest
    ) {
        const route = Routes.leads;

        return this.djangoService.drfCreate<
            DepartureLeadCreateRequest | CustomDepartureLeadCreateRequest
        >(route, data);
    }

    addDetailsToLead(id, { comment, phone_number }) {
        const route = Routes.leads;
        const resource = `${id}/add_details`;
        return this.djangoService.drfUpdate<LeadRequestAdditionalData>(
            route,
            resource,
            {
                comment,
                phone_number,
            }
        );
    }
}
